/**
 * usePagination Hook
 * Manage pagination state and helpers
 * 
 * @param {number} initialPage - Initial page number (default: 1)
 * @param {number} initialPageSize - Initial page size (default: from ENV)
 * @returns {object} - Pagination state and handlers
 * 
 * @example
 * const {
 *   page,
 *   pageSize,
 *   totalPages,
 *   setPage,
 *   setPageSize,
 *   setTotalItems,
 *   nextPage,
 *   prevPage,
 *   goToFirstPage,
 *   goToLastPage,
 *   canGoNext,
 *   canGoPrev
 * } = usePagination();
 */

import { useState, useCallback, useMemo } from 'react';
import { ENV } from '../config/environment';

const usePagination = (initialPage = 1, initialPageSize = ENV.DEFAULT_PAGE_SIZE) => {
  const [page, setPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [totalItems, setTotalItems] = useState(0);

  // Calculate total pages
  const totalPages = useMemo(() => {
    return Math.ceil(totalItems / pageSize) || 1;
  }, [totalItems, pageSize]);

  // Navigation helpers
  const canGoNext = useMemo(() => page < totalPages, [page, totalPages]);
  const canGoPrev = useMemo(() => page > 1, [page]);

  const nextPage = useCallback(() => {
    if (canGoNext) setPage(prev => prev + 1);
  }, [canGoNext]);

  const prevPage = useCallback(() => {
    if (canGoPrev) setPage(prev => prev - 1);
  }, [canGoPrev]);

  const goToFirstPage = useCallback(() => {
    setPage(1);
  }, []);

  const goToLastPage = useCallback(() => {
    setPage(totalPages);
  }, [totalPages]);

  // Handle page size change (reset to page 1)
  const handlePageSizeChange = useCallback((newSize) => {
    setPageSize((prevSize) => {
      if (prevSize !== newSize) {
        setPage(1);
      }
      return newSize;
    });
  }, []);

  // Reset pagination
  const reset = useCallback(() => {
    setPage(initialPage);
    setPageSize(initialPageSize);
    setTotalItems(0);
  }, [initialPage, initialPageSize]);

  return {
    page,
    pageSize,
    totalItems,
    totalPages,
    setPage,
    setPageSize: handlePageSizeChange,
    setTotalItems,
    nextPage,
    prevPage,
    goToFirstPage,
    goToLastPage,
    canGoNext,
    canGoPrev,
    reset
  };
};

export default usePagination;
